'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { ApiError } from '@/app/hooks/useError';
import type { ConclusionType } from '~/model/ConclusionType';
import type { InteractionType } from '~/model/InteractionType';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { SectionType } from '~/model/SectionType';
import { useCallback, useEffect, useState } from 'react';
import NotFound from '@/app/[locale]/not-found';
import { fetchInteraction } from '@/app/actions/server';
import { ApiErrorDialog } from '@/app/components/Dialog/ApiErrorDialog/ApiErrorDialog';
import { ErrorInfos } from '@/app/components/Error/ErrorInfos';
import { MainLoader } from '@/app/components/Loader';
import { ProgressProvider, useApiErrorContext } from '@/app/components/Provider';
import { SlideAnimationWrapper } from '@/app/components/SlideAnimationWrapper';
import { cannotInitializeAgain } from '@/app/utils/contributions/initialize';
import { checkIfInteractionIsAccessible } from '@/app/utils/entities/interactions';
import { getErrorCodeFromUri } from '@/app/utils/request/response';
import { entityRoutes } from '~/const/appConst';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { InteractionTypes } from '~/model/InteractionType';
import { FormProvider } from './FormProvider';
import { InteractionNotActive } from './InteractionNotActive';
import { ResultsWrapper } from './Results/ResultsWrapper';
import { ThreadWrapper } from './Thread/ThreadWrapper';
import { UserSelector } from './UserSelector';

export const InteractionWrapper = ({
  children,
  id,
  preview = false,
  results = false,
  entity = entityRoutes.survey,
}: PropsWithChildren<{
  id: number;
  preview?: boolean;
  results?: boolean;
  entity?: entityRoutes;
}>) => {
  const { error, setError } = useApiErrorContext();
  const { startRequest, handleError, handleSuccess, status } = useRequestState();

  const [interaction, setInteraction] = useState<InteractionType & ApiError>();
  const [sections, setSections] = useState<Array<SectionType>>([]);
  const [conclusions, setConclusions] = useState<Collection<ConclusionType>>();
  const [questions, setQuestions] = useState<Array<QuestionReconstructedType>>([]);

  const isAccessible = checkIfInteractionIsAccessible(interaction);
  const statusCount = interaction?.current_user_contribution?.count_per_status;

  const props = { conclusions, interaction, sections };

  const total = sections.reduce((acc, current) => {
    return (current.questions?.length || 0) + acc;
  }, (interaction?.time_slots?.length ? 1 : 0) + (interaction?.for_children ? 1 : 0));

  const getInteraction = useCallback(async () => {
    startRequest();

    const { interaction, sections, conclusions, questions } = await fetchInteraction({
      id,
      entity,
      preventRedirect: preview,
      forResults: results,
    });

    if (interaction['hydra:description'] || interaction.error_description || interaction.message) {
      handleError();
      setInteraction(interaction);
      return setError(interaction);
    }

    setInteraction(interaction);

    if (interaction['@type'] === InteractionTypes.thread) {
      handleSuccess();
      return;
    }

    setSections(sections);
    setConclusions(conclusions);

    const statusCount = interaction.current_user_contribution.count_per_status;

    if (results || (cannotInitializeAgain(interaction, statusCount) && !interaction.for_children)) {
      handleSuccess();
      return;
    }

    setQuestions(questions);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getInteraction();
  }, [getInteraction]);

  if (status === RequestStatus.PENDING || !interaction) {
    return <MainLoader />;
  }

  if (interaction['hydra:description'] || interaction.error_description || interaction.message) {
    return getErrorCodeFromUri(interaction['@id']) === 404
      ? (
          <NotFound />
        )
      : (
          <section className="centered-box">
            <div className="centered-box__content">
              <ErrorInfos error={interaction} />
            </div>
          </section>
        );
  }

  if (!isAccessible && !preview) {
    return <InteractionNotActive interaction={interaction} />;
  }

  if (interaction?.['@type'] === InteractionTypes.thread) {
    return (
      <ThreadWrapper interaction={interaction}>
        {children}
      </ThreadWrapper>
    );
  }

  if (
    !preview
    && interaction?.['@type'] === InteractionTypes.survey
    && (results || (cannotInitializeAgain(interaction, statusCount) && !interaction.for_children))
  ) {
    return (
      <SlideAnimationWrapper>
        <ResultsWrapper {...props} results={results} />
      </SlideAnimationWrapper>
    );
  }

  return (
    <ProgressProvider total={total}>
      <FormProvider {...props} questions={questions}>
        <SlideAnimationWrapper>
          <UserSelector interaction={interaction} skip={preview}>
            {children}
          </UserSelector>
        </SlideAnimationWrapper>
      </FormProvider>
      {!!error && error.code !== 401 && <ApiErrorDialog />}
    </ProgressProvider>
  );
};
